import React, { useEffect, useRef } from "react";
import {
  RequestLoaderWrapper,
  RequestDetailsContent,
  RequestDetailsContentBox,
  RequestDetailsContentBoxTitle,
  RequestDetailsContentList,
  RequestDetailsContentListItem,
  RequestDetailsHeader,
  RequestDetailsHeaderLink,
  RequestDetailsHeaderMainContent,
  RequestDetailsHeaderSecondary,
  RequestDetailsLabel,
  RequestDetailsMain,
  RequestDetailsValue,
  RequestStatusConnector,
  RequestStatusItem,
  RequestStatusPending,
  RequestStatusSuccess,
  RequestStatusWrapper,
  RequestStatusHeader,
  RefreshIconWrapper,
  RequestStatusError,
  RequestDetailsLink,
  RequestStatusItemLeft,
  RequestStatusItemRight,
  RequestStatusTitle,
  RequestItemDesc,
  RequestTableTitle,
  RequestTable,
  RequestTableEmailCol,
  RequestTableRoleCol,
  RequestTableStatusCol,
  RequestTableEmailColTd,
  RequestTableRoleColTd,
  RequestTableStatusColTd,
  RequestDetailsStatusBox,
  RequestStatusDefault,
  RequestDetailsHeaderRight,
  ETAWrapper,
} from "../style";
import { ONBOARDING_STATUS, onboardingType, REQUEST_DETAILS_TEXT, requested_at, requestDetailsItemMapping, requestDetailsLabelMapping, REQUEST_DETAILS_ETA_MAPPING } from "../../../utility/constants";
import { useDispatch, useSelector } from "react-redux";
import { callToGetRequestDetails, setRequestDetails } from "../ducks/slice";
import { useParams } from "react-router-dom";
import {
  getIsLoadingRequestDetails,
  getIsLoadingRequestStatus,
  getRequestDetails,
} from "../ducks/selectors";
import { formatDateTime } from "utility/data-formatters";
import { CircularProgress, Skeleton } from "@mui/material";
import { ENVMAPPING } from "utility/helper";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import StageEnvStatusTracker from './StageEnvStatusTracker';
import { convertSnakeToTitleCase } from "services/utilityService";
import {
  CLOUD_SERVICE_PAGE_CONSTANTS,
} from "utility/constants";
import { isArrayOfStringsOrNumbers } from "pages/mlops/ducks/utils";

const REQUEST_DETAIL_AUTO_REFRESH_TIME = 15 * 1000 // 15 seconds
let requestAutoRefreshInterval: ReturnType<typeof setInterval> | undefined;

function RequestDetails() {
  const dispatch = useDispatch();
  const params = useParams();
  const requestDetail = useSelector(getRequestDetails);
  const isLoadingRequestDetails = useSelector(getIsLoadingRequestDetails);
  const isLoadingRequestStatus = useSelector(getIsLoadingRequestStatus);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      dispatch(setRequestDetails(null));
      dispatch(
        callToGetRequestDetails({ requestId: params.requestId as string })
      );
      isMounted.current = true;
    }
  }, [isMounted, dispatch, params?.requestId]);

  useEffect(() => {
    requestAutoRefreshInterval = setInterval(() => {
      dispatch(callToGetRequestDetails({ requestId: params.requestId as string, isSilentRefresh: true, isRefreshStatusFlow: false }));
    }, REQUEST_DETAIL_AUTO_REFRESH_TIME)
    return () => {
      if (requestAutoRefreshInterval) { clearInterval(requestAutoRefreshInterval); }
    }
  }, [dispatch, params.requestId])

  const onClickRefresh = () => {
    dispatch(
      callToGetRequestDetails({
        requestId: params.requestId as string,
        isRefreshStatusFlow: true,
      })
    );
  };

  const preLineFields = ["business_justification", "comments"];
  const successStatus = ["completed", "success", "approved", "complete"];
  const requestOtherThanPending = ["completed", "success", "approved", "complete", "failed", "rejected"];
  const requestNodes = {
    success: (
      <RequestStatusSuccess>
        <img
          width={18}
          height={13.55}
          alt="success"
          src="/assets/success-icon.svg"
        ></img>
      </RequestStatusSuccess>
    ),
    failed: (
      <RequestStatusError>
        <img
          width={18}
          height={18}
          alt="pending"
          src="/assets/error-icon.svg"
        ></img>
      </RequestStatusError>
    ),
    pending: (
      <RequestStatusPending>
        <img
          width={14}
          height={14}
          alt="pending"
          src="/assets/pending.svg"
        ></img>
      </RequestStatusPending>
    ),
    default: (
      <RequestStatusDefault>
        <img
          width={18}
          height={18}
          alt="pending"
          src="/assets/pending.svg"
        ></img>
      </RequestStatusDefault>
    ),
  };

  const getOneTSTrackRequestLink = (request_sys_id: string) => {
    return `${ENVMAPPING(window.location.origin).track_request_url
      }/1ts?id=1ts_requests&table=sc_req_item&sys_id=${request_sys_id}&view=sp`;
  };

  const requestType = {
    user_onboarding_add: "user_onboarding",
    user_onboarding_remove: "user_onboarding",
    pod_onboarding: "pod_onboarding",
    cloud_request: "cloud_request"
  }

  const getETAForStage = (requestCreatedAt: string, stageName: string) => {
    console.log("requestCreatedAt", requestCreatedAt);
    const createdAtDate = moment(requestCreatedAt);
    const requestTypeValue = requestType[requestDetail?.onboarding_type as keyof typeof requestType]
    const itemETA = REQUEST_DETAILS_ETA_MAPPING[stageName as keyof typeof REQUEST_DETAILS_ETA_MAPPING];
    const dateETA = createdAtDate.add(itemETA[requestTypeValue as keyof typeof itemETA].day, 'days');
    return dateETA.format("MM/DD/YYYY")
  }

  const onboardingTitle = onboardingType[
    requestDetail?.onboarding_type as keyof typeof onboardingType
  ];

  const renderRequestDetails = () => {
    if (!requestDetail?.request_details) {
      return null;
    }

    const requestDetailsItems = Object.entries(requestDetail.request_details)
      .filter(([key, _value]) => {
        return !(
          requestDetail?.onboarding_type === "cloud_request" &&
          key === "user_role"
        );
      })
      .map(([key, value], index) => (
        <RequestDetailsContentListItem key={`request-detail-${index + 1}`}>
          <RequestDetailsLabel>
            {requestDetailsLabelMapping[
              key as keyof typeof requestDetailsLabelMapping
            ] || key}
          </RequestDetailsLabel>
          <RequestDetailsValue isWhiteSpacePreline={preLineFields.includes(key)}>
            {value.trim()}
          </RequestDetailsValue>
        </RequestDetailsContentListItem>
      ));

    let cloudRequestDetailsItems: any = [];
    let cloudDataHeading = null;

    const cloudRequestDetails = requestDetail.cloud_request_details;

    if (
      cloudRequestDetails &&
      Object.keys(cloudRequestDetails).length > 0
    ) {
      cloudDataHeading = (
        <RequestDetailsContentListItem key="cloud-data-heading">
          <RequestDetailsLabel style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px' }}>
            {CLOUD_SERVICE_PAGE_CONSTANTS.trackCloudRequestsTitle}
          </RequestDetailsLabel>
          <RequestDetailsValue></RequestDetailsValue>
        </RequestDetailsContentListItem>
      );

      cloudRequestDetailsItems = Object.entries(cloudRequestDetails).map(([key, value]) => {
        const isArrayPayload = Array.isArray(value);
        let content: React.ReactNode;

        if (isArrayPayload) {
          if (isArrayOfStringsOrNumbers(value)) {
            // value is an array of strings
            content = (
              <React.Fragment>
                <RequestDetailsLabel style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {convertSnakeToTitleCase(key)}
                </RequestDetailsLabel>
                <RequestDetailsValue isWhiteSpacePreline={preLineFields.includes(key)}>
                  {value.join(", ")}
                </RequestDetailsValue>
              </React.Fragment>)
          } else {
            // value is an array of objects
            content = (value as Record<string, unknown>[]).map((item, itemIdx) => (
              <React.Fragment key={`cloud-request-detail-${key}-${itemIdx}`}>
                <RequestDetailsLabel style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {convertSnakeToTitleCase(key) + " - " + (itemIdx + 1)}
                </RequestDetailsLabel>
                {Object.entries(item).map(([subKey, subValue]) => (
                  <React.Fragment key={`cloud-request-detail-${key}-${itemIdx}-${subKey}`}>
                    <RequestDetailsLabel>
                      {convertSnakeToTitleCase(requestDetailsLabelMapping[
                        subKey as keyof typeof requestDetailsLabelMapping
                      ]) || convertSnakeToTitleCase(subKey)}
                    </RequestDetailsLabel>
                    <RequestDetailsValue isWhiteSpacePreline={preLineFields.includes(key)}>
                      {String(subValue).trim()}
                    </RequestDetailsValue>
                  </React.Fragment>
                ))}
              </React.Fragment>
            ));
          }
        } else {
          content = (
            <>
              <RequestDetailsLabel>
                {convertSnakeToTitleCase(requestDetailsLabelMapping[
                  key as keyof typeof requestDetailsLabelMapping
                ]) || convertSnakeToTitleCase(key)}
              </RequestDetailsLabel>
              <RequestDetailsValue isWhiteSpacePreline={preLineFields.includes(key)}>
                {String(value).trim()}
              </RequestDetailsValue>
            </>
          );
        }

        return (
          <RequestDetailsContentListItem key={`cloud-request-detail-${key}`}>
            {content}
          </RequestDetailsContentListItem>
        );
      });
    }

    return [
      ...requestDetailsItems,
      ...(cloudDataHeading ? [cloudDataHeading] : []),
      ...cloudRequestDetailsItems,
    ];
  };


  return (
    <RequestDetailsMain>
      {
        !isLoadingRequestDetails &&
        <RequestDetailsHeader>
          <div>
            <RequestDetailsHeaderMainContent>
              {onboardingTitle}{" "}
              <RequestDetailsHeaderLink
                target="_blank"
                href={getOneTSTrackRequestLink(
                  requestDetail?.request_sys_id as string
                )}
              >
                {requestDetail?.ritm_number as string}
              </RequestDetailsHeaderLink>
            </RequestDetailsHeaderMainContent>
            <RequestDetailsHeaderSecondary>
              {requested_at} {formatDateTime(requestDetail?.request_at)}
            </RequestDetailsHeaderSecondary></div>

          <RequestDetailsHeaderRight>
            <span>Need any help ?</span>
            <br /> Contact <a href="mailto:DataSciencePlatform@toyota.com">DataSciencePlatform@toyota.com</a>
          </RequestDetailsHeaderRight>
        </RequestDetailsHeader>
      }
      <RequestDetailsContent>
        <RequestDetailsContentBox>
          <RequestDetailsContentList>
            {isLoadingRequestDetails
              ? Array.from(Array(6).keys()).map((_, key) => (
                <Skeleton
                  key={key + 1}
                  variant="text"
                  sx={{ fontSize: "1rem" }}
                />
              ))
              : renderRequestDetails()}
          </RequestDetailsContentList>
        </RequestDetailsContentBox>
        <RequestDetailsStatusBox>
          <RequestStatusHeader>
            <RequestDetailsContentBoxTitle>
              Request Status
            </RequestDetailsContentBoxTitle>
            <RefreshIconWrapper onClick={onClickRefresh}>
              <RefreshIcon fontSize="small" />
            </RefreshIconWrapper>
          </RequestStatusHeader>
          <RequestStatusWrapper>
            {isLoadingRequestDetails || isLoadingRequestStatus ? (
              <RequestLoaderWrapper>
                <CircularProgress size={40} />
              </RequestLoaderWrapper>
            ) : (
              requestDetail?.request_status.map((requestItem, index) => {
                const stageName = requestDetailsItemMapping[
                  requestItem.request_item as keyof typeof requestDetailsItemMapping
                ]
                return (
                  <div key={index + 1}>
                    <RequestStatusItem>
                      {
                        <RequestStatusItemLeft>
                          {
                            requestDetail.request_stage === requestItem.request_item && !requestOtherThanPending.includes(requestItem.status)
                              ? requestNodes["pending"] :
                              requestNodes[
                              successStatus.includes(
                                requestItem.status.toLowerCase()
                              )
                                ? "success"
                                : (requestItem.status.toLowerCase() === "failed" || requestItem.status.toLowerCase() === "rejected")
                                  ? "failed"
                                  : "default"
                              ]
                          }

                          {requestDetail.request_status.length - 1 !== index && (
                            <RequestStatusConnector></RequestStatusConnector>
                          )}
                        </RequestStatusItemLeft>
                      }
                      <RequestStatusItemRight>
                        <RequestStatusTitle>
                          <span>
                            {stageName === requestDetailsItemMapping.azure_onboarding && onboardingTitle === onboardingType.user_onboarding_remove ? requestDetailsItemMapping.azure_offboarding : stageName}
                          </span>
                          <ETAWrapper>ETA: {getETAForStage(requestDetail?.request_at, requestItem.request_item)}</ETAWrapper>
                        </RequestStatusTitle>
                        {requestItem?.meta_data?.request_sys_id &&
                          requestItem?.meta_data?.ritm_number && (
                            <RequestItemDesc>
                              {REQUEST_DETAILS_TEXT}{" "}
                              <RequestDetailsLink
                                target="_blank"
                                href={getOneTSTrackRequestLink(
                                  requestItem?.meta_data.request_sys_id as string
                                )}
                              >
                                {requestItem?.meta_data?.ritm_number}
                              </RequestDetailsLink>
                            </RequestItemDesc>
                          )}
                        {
                          (requestItem?.meta_data && requestItem?.meta_data?.updated_at) ? <RequestItemDesc>Updated at:{formatDateTime(requestItem?.meta_data?.updated_at)}</RequestItemDesc> : ""
                        }
                        {(requestItem.request_item === "1ts" || requestItem.request_item === "manager_approval") && (
                          <RequestItemDesc>
                            {REQUEST_DETAILS_TEXT}{" "}
                            <RequestDetailsLink
                              target="_blank"
                              href={getOneTSTrackRequestLink(
                                requestDetail?.request_sys_id as string
                              )}
                            >
                              {requestDetail?.ritm_number as string}
                            </RequestDetailsLink>
                          </RequestItemDesc>
                        )}
                        {
                          requestItem?.meta_data?.manager_name ? <RequestItemDesc>Manager Name: {requestItem?.meta_data?.manager_name}</RequestItemDesc> : ""
                        }
                        {
                          requestItem?.meta_data?.manager_email ? <RequestItemDesc>Manager Email: <RequestDetailsLink href={`mailto:${requestItem?.meta_data?.manager_email}`}>{requestItem?.meta_data?.manager_email}</RequestDetailsLink></RequestItemDesc> : ""
                        }
                        <StageEnvStatusTracker
                          requestItem={requestItem}
                          requestDetail={requestDetail}
                          successStatus={successStatus}
                          requestNodes={requestNodes}
                          request_number={requestDetail?.request_number}
                          req_ambassador_email={requestDetail?.request_details?.ambassador_email}
                        />
                        {requestItem.request_item === "onboarding_users" &&
                          requestItem?.meta_data?.data &&
                          requestItem?.meta_data?.data?.length > 0 && (
                            <div>
                              <RequestTableTitle>
                                {ONBOARDING_STATUS}:{" "}
                              </RequestTableTitle>
                              <RequestTable key={index + 1}>
                                <tr>
                                  <RequestTableEmailCol>
                                    Email
                                  </RequestTableEmailCol>
                                  <RequestTableRoleCol>Role</RequestTableRoleCol>
                                  <RequestTableStatusCol>
                                    Status
                                  </RequestTableStatusCol>
                                </tr>
                                {requestItem?.meta_data?.data.map(
                                  (
                                    userItem: {
                                      email: string;
                                      role: string;
                                      status: string;
                                    },
                                    index: number
                                  ) => (
                                    <tr key={index + 1}>
                                      <RequestTableEmailColTd>
                                        {userItem.email}
                                      </RequestTableEmailColTd>
                                      <RequestTableRoleColTd>
                                        {userItem.role}
                                      </RequestTableRoleColTd>
                                      <RequestTableStatusColTd>
                                        {userItem.status}
                                      </RequestTableStatusColTd>
                                    </tr>
                                  )
                                )}
                              </RequestTable>
                            </div>
                          )}
                      </RequestStatusItemRight>
                    </RequestStatusItem>
                  </div>
                )
              })
            )}
          </RequestStatusWrapper>
        </RequestDetailsStatusBox>
      </RequestDetailsContent>
    </RequestDetailsMain>
  );
}

export default RequestDetails;
